'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import clsx from 'clsx';
import { dequal } from 'dequal';
import makeEventProps from 'make-event-props';
import * as pdfjs from 'pdfjs-dist';
import invariant from 'tiny-invariant';
import warning from 'warning';
import DocumentContext from './DocumentContext.js';
import LinkService from './LinkService.js';
import Message from './Message.js';
import PasswordResponses from './PasswordResponses.js';
import useResource from './shared/hooks/useResource.js';
import areDocumentInputsEqual from './shared/areDocumentInputsEqual.js';
import ResourceCache from './shared/ResourceCache.js';
import { dataURItoByteString, displayCORSWarning, isArrayBuffer, isBlob, isBrowser, isDataURI, loadFromFile, } from './shared/utils.js';
const { PDFDataRangeTransport } = pdfjs;
const defaultOnPassword = (callback, reason) => {
    switch (reason) {
        case PasswordResponses.NEED_PASSWORD: {
            const password = prompt('Enter the password to open this PDF file.');
            callback(password);
            break;
        }
        case PasswordResponses.INCORRECT_PASSWORD: {
            const password = prompt('Invalid password. Please try again.');
            callback(password);
            break;
        }
        default:
    }
};
function isParameterObject(file) {
    return (typeof file === 'object' &&
        file !== null &&
        ('data' in file || 'range' in file || 'url' in file));
}
async function findDocumentSource(file) {
    if (!file) {
        return null;
    }
    // File is a string
    if (typeof file === 'string') {
        if (isDataURI(file)) {
            const fileByteString = dataURItoByteString(file);
            return { data: fileByteString };
        }
        displayCORSWarning();
        return { url: file };
    }
    // File is PDFDataRangeTransport
    if (file instanceof PDFDataRangeTransport) {
        return { range: file };
    }
    // File is an ArrayBuffer
    if (isArrayBuffer(file)) {
        return { data: file };
    }
    /**
     * The cases below are browser-only.
     * If you're running on a non-browser environment, these cases will be of no use.
     */
    if (isBrowser) {
        // File is a Blob
        if (isBlob(file)) {
            const data = await loadFromFile(file);
            return { data };
        }
    }
    // At this point, file must be an object
    invariant(typeof file === 'object', 'Invalid parameter in file, need either Uint8Array, string or a parameter object');
    invariant(isParameterObject(file), 'Invalid parameter object: need either .data, .range or .url');
    // File .url is a string
    if ('url' in file && typeof file.url === 'string') {
        if (isDataURI(file.url)) {
            const { url, ...otherParams } = file;
            const fileByteString = dataURItoByteString(url);
            return { data: fileByteString, ...otherParams };
        }
        displayCORSWarning();
    }
    return file;
}
const sourceCache = new ResourceCache({
    areKeysEqual: areDocumentInputsEqual,
});
const documentCache = new ResourceCache({
    areKeysEqual: areDocumentInputsEqual,
});
function createDocumentTask(source, options, onPassword, onLoadProgress) {
    const initParameters = options ? { ...source, ...options } : source;
    const parameters = { ...initParameters };
    // PDF.js transfers input buffers to its worker. Keep the cached source reusable
    // after eviction, and when another document uses the same input.
    if (parameters.data instanceof ArrayBuffer) {
        parameters.data = parameters.data.slice(0);
    }
    else if (ArrayBuffer.isView(parameters.data)) {
        const data = parameters.data;
        parameters.data = new Uint8Array(data.buffer, data.byteOffset, data.byteLength).slice();
    }
    const task = pdfjs.getDocument(parameters);
    task.onPassword = onPassword;
    if (onLoadProgress) {
        task.onProgress = onLoadProgress;
    }
    return task;
}
/**
 * Loads a document passed using `file` prop.
 */
const Document = forwardRef(function Document({ children, className, error = 'Failed to load PDF file.', externalLinkRel, externalLinkTarget, file, inputRef, imageResourcesPath, loading = 'Loading PDF…', noData = 'No PDF file specified.', onItemClick, onLoadError: onLoadErrorProps, onLoadProgress, onLoadSuccess: onLoadSuccessProps, onPassword = defaultOnPassword, onSourceError: onSourceErrorProps, onSourceSuccess: onSourceSuccessProps, options, renderMode, rotate, scale, suspense = true, ...otherProps }, ref) {
    const loadSource = useCallback(() => ({ promise: findDocumentSource(file) }), [file]);
    const { value: source, error: sourceError } = useResource(!suspense || file ? { cache: sourceCache, key: [file], load: loadSource } : undefined, suspense, onSourceErrorProps);
    // biome-ignore lint/correctness/useExhaustiveDependencies: Callback changes should not restart the document load
    const loadPdf = useMemo(() => {
        if (!source) {
            return;
        }
        return () => {
            const task = createDocumentTask(source, options, onPassword, onLoadProgress);
            return { promise: task.promise, dispose: () => task.destroy() };
        };
    }, [options, source, suspense]);
    const { value: pdf, error: pdfError } = useResource(loadPdf ? { cache: documentCache, key: [file, options], load: loadPdf } : undefined, suspense, onLoadErrorProps);
    const linkService = useRef(new LinkService());
    const pages = useRef([]);
    const prevFile = useRef(undefined);
    const prevOptions = useRef(undefined);
    if (!suspense && file && file !== prevFile.current && isParameterObject(file)) {
        warning(!dequal(file, prevFile.current), `File prop passed to <Document /> changed, but it's equal to previous one. This might result in unnecessary reloads. Consider memoizing the value passed to "file" prop.`);
        prevFile.current = file;
    }
    // Detect non-memoized changes in options prop
    if (!suspense && options && options !== prevOptions.current) {
        warning(!dequal(options, prevOptions.current), `Options prop passed to <Document /> changed, but it's equal to previous one. This might result in unnecessary reloads. Consider memoizing the value passed to "options" prop.`);
        prevOptions.current = options;
    }
    const viewer = useRef({
        // Handling jumping to internal links target
        scrollPageIntoView: (args) => {
            const { dest, pageNumber, pageIndex = pageNumber - 1 } = args;
            // First, check if custom handling of onItemClick was provided
            if (onItemClick) {
                onItemClick({ dest, pageIndex, pageNumber });
                return;
            }
            // If not, try to look for target page within the <Document>.
            const page = pages.current[pageIndex];
            if (page) {
                // Scroll to the page automatically
                page.scrollIntoView();
                return;
            }
            warning(false, `An internal link leading to page ${pageNumber} was clicked, but neither <Document> was provided with onItemClick nor it was able to find the page within itself. Either provide onItemClick to <Document> and handle navigating by yourself or ensure that all pages are rendered within <Document>.`);
        },
    });
    useImperativeHandle(ref, () => ({
        linkService,
        pages,
        viewer,
    }), []);
    /**
     * Called when a document source is resolved correctly
     */
    function onSourceSuccess() {
        if (onSourceSuccessProps) {
            onSourceSuccessProps();
        }
    }
    /**
     * Called when a document source failed to be resolved correctly
     */
    function onSourceError() {
        if (!sourceError) {
            // Impossible, but TypeScript doesn't know that
            return;
        }
        warning(false, sourceError.toString());
        if (onSourceErrorProps) {
            onSourceErrorProps(sourceError);
        }
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: Omitted callbacks so they are not called every time they change
    useEffect(() => {
        if (typeof source === 'undefined') {
            return;
        }
        if (source === false) {
            onSourceError();
            return;
        }
        onSourceSuccess();
    }, [source]);
    /**
     * Called when a document is read successfully
     */
    function onLoadSuccess() {
        if (!pdf) {
            // Impossible, but TypeScript doesn't know that
            return;
        }
        if (onLoadSuccessProps) {
            onLoadSuccessProps(pdf);
        }
        linkService.current.setDocument(pdf);
    }
    /**
     * Called when a document failed to read successfully
     */
    function onLoadError() {
        if (!pdfError) {
            // Impossible, but TypeScript doesn't know that
            return;
        }
        warning(false, pdfError.toString());
        if (onLoadErrorProps) {
            onLoadErrorProps(pdfError);
        }
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: Omitted callbacks so they are not called every time they change
    useEffect(() => {
        if (typeof pdf === 'undefined') {
            return;
        }
        if (pdf === false) {
            onLoadError();
            return;
        }
        onLoadSuccess();
    }, [pdf]);
    useEffect(function setupLinkService() {
        linkService.current.setViewer(viewer.current);
        linkService.current.setExternalLinkRel(externalLinkRel);
        linkService.current.setExternalLinkTarget(externalLinkTarget);
    }, [externalLinkRel, externalLinkTarget]);
    const registerPage = useCallback((pageIndex, ref) => {
        pages.current[pageIndex] = ref;
    }, []);
    const unregisterPage = useCallback((pageIndex) => {
        delete pages.current[pageIndex];
    }, []);
    const childContext = useMemo(() => ({
        imageResourcesPath,
        linkService: linkService.current,
        onItemClick,
        pdf,
        registerPage,
        renderMode,
        rotate,
        scale,
        suspense,
        unregisterPage,
    }), [
        imageResourcesPath,
        onItemClick,
        pdf,
        registerPage,
        renderMode,
        rotate,
        scale,
        suspense,
        unregisterPage,
    ]);
    const eventProps = useMemo(() => makeEventProps(otherProps, () => pdf), 
    // biome-ignore lint/correctness/useExhaustiveDependencies: FIXME
    [otherProps, pdf]);
    function renderChildren() {
        function isFulfilledContext(context) {
            return Boolean(context === null || context === void 0 ? void 0 : context.pdf);
        }
        if (!isFulfilledContext(childContext)) {
            // Impossible, but TypeScript doesn't know that
            throw new Error('pdf is undefined');
        }
        const resolvedChildren = typeof children === 'function' ? children(childContext) : children;
        return (_jsx(DocumentContext.Provider, { value: childContext, children: resolvedChildren }));
    }
    function renderContent() {
        if (!file) {
            return _jsx(Message, { type: "no-data", children: typeof noData === 'function' ? noData() : noData });
        }
        if (pdf === undefined || pdf === null) {
            return (_jsx(Message, { type: "loading", children: typeof loading === 'function' ? loading() : loading }));
        }
        if (pdf === false) {
            return _jsx(Message, { type: "error", children: typeof error === 'function' ? error() : error });
        }
        return renderChildren();
    }
    return (_jsx("div", { className: clsx('react-pdf__Document', className), ref: inputRef, ...eventProps, children: renderContent() }));
});
export default Document;
